import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chip from "../Chip";
import KeywordsDropdown from "./KeywordsDropdown";
import DropdownWrapper from "../dropdown/DropdownWrapper";
import useDebounce from "../../hooks/useDebounce";
import { IKeywords } from "../../types/keywords";
import useCheckShadow from "../../hooks/useCheckShadow";
import {
  fetchRecentSearches,
  searchKeywordsApi,
  suggestedKeywordsApi,
} from "../../apis/apis";
import {
  fixSingleQuotes,
  isValidKeyword,
  processCommaSeparatedValues,
} from "../../utils/keywordsInputHandler";

interface IProps {
  selectedKeywords: IKeywords[];
  setSelectedKeywords: React.Dispatch<React.SetStateAction<IKeywords[]>>;
  middleware?: string;
  userDetails: any;
  isError?: boolean;
  handleDropdownOpen: (open: boolean) => void;
  isActive?: boolean;
  hasActiveField?: boolean;
  handleFieldActive: (field: 'keywords' | 'location' | 'exp' | null) => void;
}

export const TOOLTIP_ID = "qs-keyword-chip-star-tooltip";


const Keywords: React.FC<IProps> = ({
  selectedKeywords,
  setSelectedKeywords,
  middleware = "",
  userDetails,
  isError,
  handleDropdownOpen,
  isActive = false,
  hasActiveField = false,
  handleFieldActive,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [editingChipId, setEditingChipId] = useState<string | null>(null);
  const [editingChipValue, setEditingChipValue] = useState<string>("");
  const [displayPlaceholder, setDisplayPlaceholder] = useState<string>(
    selectedKeywords?.length
      ? "Type another keyword"
      : "Enter keywords like skills and job titles"
  );

  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const debouncedInputValue = useDebounce(inputValue, 300);
  const chipsRef = useRef<HTMLDivElement>(null);
  const mainInputRef = useRef<HTMLInputElement>(null);
  const prevEditingChipIdRef = useRef<string | null>(null);
  const isClickingDropdownRef = useRef<boolean>(false);

  const { showLeftShadow, showRightShadow } = useCheckShadow({
    ref: chipsRef,
    scrollBasedOn: [selectedKeywords?.length],
  });

  const dropdownType = useMemo(() => {
    if (inputValue) {
      return "search";
    }
    if (!inputValue && !selectedKeywords?.length) {
      return "recentSearches";
    }
    if (selectedKeywords?.length) {
      return "suggestion";
    }
    return "recentSearches";
  }, [inputValue, selectedKeywords?.length]);

  const dropdownItems = useMemo(() => {
    switch (dropdownType) {
      case "recentSearches":
        return recentSearches;
      case "search":
        return searchResults;
      case "suggestion":
        return suggestions;
      default:
        return [];
    }
  }, [dropdownType, recentSearches, searchResults, suggestions]);

  const inputPlaceholder = useMemo(() => {
    if (editingChipId) {
      return "";
    }
    return selectedKeywords?.length
      ? "Type another keyword"
      : "Enter keywords like skills and job titles";
  }, [editingChipId, selectedKeywords?.length]);

  // Helper function to check if response contains HTML
  const isHtmlResponse = (result: string[]): boolean => {
    if (!Array.isArray(result) || result.length === 0) {
      return false;
    }
    // Check if any string in the array looks like HTML
    return result.some((item) => {
      if (typeof item !== "string") return false;
      const trimmed = item.trim();
      return (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<HTML") ||
        (trimmed.includes("<html") && trimmed.includes("</html>"))
      );
    });
  };

  const searchKeywords = useCallback(async (query: string) => {
    try {
      const result = await searchKeywordsApi({ query, middleware });
      // Don't update state if response contains HTML
      if (result && !isHtmlResponse(result)) {
        setSearchResults(result);
      }
    } catch (error) {
      // console.error("Error in searchKeywords:", error);
      setSearchResults([]);
    }
  }, [middleware]);

  const suggestedKeywords = async (query: string) => {
    try {
      const result = await suggestedKeywordsApi({ query, middleware });
      // Don't update state if response contains HTML
      if (result && !isHtmlResponse(result)) {
        setSuggestions(result);
      }
    } catch (error) {
      // console.error("Error in suggestedKeywords:", error);
      setSuggestions([]);
    }
  };

  const handleSelect = (value: string) => {
    const processedValue = fixSingleQuotes(value.trim());

    if (
      processedValue &&
      isValidKeyword(processedValue) &&
      !selectedKeywords.find((chip) => chip.text === processedValue)
    ) {
      setSelectedKeywords([
        ...selectedKeywords,
        {
          id: processedValue,
          text: processedValue,
          isActive: selectedKeywords.length === 0,
        },
      ]);
    }
    setInputValue("");
    // focus on input
    mainInputRef.current?.focus();

    suggestedKeywords(processedValue);
  };

  // Handle multiple keywords from comma-separated input
  const handleMultipleSelect = (values: string[]) => {
    // First, remove duplicates within the pasted values
    const uniqueValues = Array.from(
      new Set(
        values
          .map((value) => value.trim())
          .filter((value) => value && isValidKeyword(value))
      )
    );

    // Then filter out values that already exist
    const validValues = uniqueValues.filter(
      (value) => !selectedKeywords.find((chip) => chip.text === value)
    );

    if (validValues.length > 0) {
      setSelectedKeywords((prev) => [
        ...prev,
        ...validValues.map((value, index) => ({
          id: value,
          text: value,
          isActive: prev.length === 0 && index === 0,
        })),
      ]);
    }
    setInputValue("");
  };

  const handleDelete = (id: string) => {
    setSelectedKeywords(selectedKeywords.filter((chip) => chip.id !== id));
  };

  const toggleActive = (id: string) => {
    setSelectedKeywords((prev) =>
      prev.map((chip) => ({
        ...chip,
        isActive: chip.id === id ? !chip.isActive : chip.isActive,
      }))
    );
  };

  const handleChipEdit = (id: string) => {
    const chip = selectedKeywords.find((c) => c.id === id);
    if (chip) {
      setEditingChipId(id);
      setEditingChipValue(chip.text);
    }
  };

  const handleChipEditFinish = (id: string) => {
    const trimmedValue = editingChipValue.trim();

    if (!trimmedValue) {
      // Remove chip if empty
      handleDelete(id);
    } else {
      // Update chip if value is valid and not duplicate
      const isDuplicate = selectedKeywords.some(
        (chip) => chip.id !== id && chip.text === trimmedValue
      );

      if (!isDuplicate && isValidKeyword(trimmedValue)) {
        const processedValue = fixSingleQuotes(trimmedValue);
        setSelectedKeywords((prev) =>
          prev.map((chip) =>
            chip.id === id
              ? { ...chip, id: processedValue, text: processedValue }
              : chip
          )
        );
      }
    }

    setEditingChipId(null);
    setEditingChipValue("");
  };

  const handleChipEditCancel = () => {
    setEditingChipId(null);
    setEditingChipValue("");
  };

  const handleCloseDropdown = () => {
    if (inputValue.trim()) {
      if (inputValue.includes(",")) {
        const commaSeparatedValues = processCommaSeparatedValues(inputValue);

        if (commaSeparatedValues.length > 0) {
          handleMultipleSelect(commaSeparatedValues);
        }
      } else {
        handleSelect(inputValue.trim());
      }
      setInputValue("");
    }
    setShowDropdown(false);
    handleFieldActive(null);
  };

  const handleFocus = () => {
    setShowDropdown(true);
    handleDropdownOpen(true);
    handleFieldActive('keywords');
  };

  // fetch recent searches
  // useEffect(() => {
  //   const fetch = async () => {
  //     const response = await fetchRecentSearches({ middleware, userDetails });
  //     setRecentSearches(response);
  //   };

  //   if (middleware && userDetails) {
  //     fetch();
  //   }
  // }, [middleware, userDetails]);

  // prevents placeholder flicker when switching between editing different chips
  useEffect(() => {
    const timer = setTimeout(() => {
      setDisplayPlaceholder(inputPlaceholder);
    }, 150);
    return () => clearTimeout(timer);
  }, [inputPlaceholder]);

  // focus on input on mount
  useEffect(() => {
    if (mainInputRef.current) {
      mainInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    // focus main input when chip edit is finished
    if (
      prevEditingChipIdRef.current !== null &&
      editingChipId === null &&
      mainInputRef.current
    ) {
      const timer = setTimeout(() => {
        mainInputRef.current?.focus();
      }, 100);

      return () => clearTimeout(timer);
    }
    prevEditingChipIdRef.current = editingChipId;
  }, [editingChipId]);

  // for keywords search
  useEffect(() => {
    if (debouncedInputValue && debouncedInputValue.trim()) {
      searchKeywords(debouncedInputValue);
    } else {
      setSearchResults([]);
    }
  }, [debouncedInputValue, searchKeywords]);

  // Reset selected index when dropdown items change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [dropdownItems]);

  // Attach mousedown listener to dropdown to prevent blur when clicking items
  useEffect(() => {
    if (!showDropdown || !dropdownItems?.length) return;

    const handleMouseDown = (e: MouseEvent) => {
      // Set flag when mousedown happens on dropdown
      const target = e.target as HTMLElement;
      const dropdownWrapper = target.closest('.qs_dd_wrapper');
      if (dropdownWrapper) {
        isClickingDropdownRef.current = true;
      }
    };

    // Use capture phase to catch the event early
    document.addEventListener('mousedown', handleMouseDown, true);
    
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
    };
  }, [showDropdown, dropdownItems]);

  useEffect(()=>{
    if(!showDropdown || !dropdownItems?.length){
      setTimeout(()=>{
        handleDropdownOpen(false);
      },200)
    }else if(showDropdown && !!dropdownItems?.length){
      handleDropdownOpen(true);
    }
  },[showDropdown, dropdownItems])

  const className = `qs_search_input_sec keywords${isActive ? ' active' : hasActiveField ? ' inactive' : ''}`;

  return (
    <div className={className}>
      <div className="qsSprite icons search_black" style={{ flexShrink: 0 }} />
      <div className="qs_search_chip_container_wrapper">
        {showLeftShadow && <div className="qs_overflow_shadow left" />}
        <div className="qs_search_chip_container" ref={chipsRef}>
          {/* Chips  */}
          {!!selectedKeywords?.length && (
            <div className="qs_search_chips">
              {selectedKeywords?.map((chip) => {
                if (editingChipId === chip.id) {
                  return (
                    <div
                      key={chip.id}
                      className="qs_search_input_container"
                      style={{ display: "inline-block" }}
                    >
                      <input
                        type="text"
                        autoComplete="off"
                        value={editingChipValue}
                        onChange={(e) => setEditingChipValue(e.target.value)}
                        onBlur={() => handleChipEditFinish(chip.id)}
                        onPaste={(e) => {
                          // Handle paste with comma-separated values in edit mode
                          const pastedText = e.clipboardData.getData("text");

                          if (pastedText.includes(",")) {
                            e.preventDefault();
                            const commaSeparatedValues =
                              processCommaSeparatedValues(pastedText);

                            // First, remove duplicates within the pasted values
                            const uniquePastedValues = Array.from(
                              new Set(
                                commaSeparatedValues
                                  .map((value) => value.trim())
                                  .filter(
                                    (value) => value && isValidKeyword(value)
                                  )
                              )
                            );

                            // Then filter out values that already exist (excluding current chip)
                            const newValues = uniquePastedValues.filter(
                              (value) =>
                                !selectedKeywords.find(
                                  (c) => c.id !== chip.id && c.text === value
                                )
                            );

                            if (newValues.length > 0) {
                              // Update current chip with first new value
                              const firstValue = newValues[0];
                              const processedValue =
                                fixSingleQuotes(firstValue);
                              setSelectedKeywords((prev) =>
                                prev.map((c) =>
                                  c.id === chip.id
                                    ? {
                                        ...c,
                                        id: processedValue,
                                        text: processedValue,
                                      }
                                    : c
                                )
                              );

                              // Add remaining new values as new chips
                              if (newValues.length > 1) {
                                const remainingValues = newValues.slice(1);
                                setSelectedKeywords((prev) => [
                                  ...prev,
                                  ...remainingValues.map((value) => ({
                                    id: value,
                                    text: fixSingleQuotes(value),
                                    isActive: false,
                                  })),
                                ]);
                              }

                              // Exit edit mode
                              setEditingChipId(null);
                              setEditingChipValue("");
                            } else {
                              // All values already exist, just exit edit mode
                              setEditingChipId(null);
                              setEditingChipValue("");
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleChipEditFinish(chip.id);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            handleChipEditCancel();
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="qs_search_input"
                        autoFocus
                        size={Math.max(editingChipValue.length || 1, 5)}
                        style={{ width: "auto" }}
                      />
                    </div>
                  );
                }
                return (
                  <Chip
                    key={chip.id}
                    chipDetails={{ id: chip.id, text: chip.text }}
                    onDelete={handleDelete}
                    onClick={toggleActive}
                    onTextClick={handleChipEdit}
                    isActive={chip.isActive}
                    showRequiredMark
                  />
                );
              })}
            </div>
          )}

          {/* Input  */}
          <div className="qs_search_input_container">
            <input
              ref={mainInputRef}
              value={inputValue}
              onChange={(e) => {
                const value = e.target.value;
                setInputValue(value);
                setShowDropdown(true);
                handleDropdownOpen(true);
                handleFieldActive('keywords');

                if (value.endsWith(",")) {
                  const textBeforeComma = value.slice(0, -1).trim();
                  if (textBeforeComma) {
                    handleSelect(textBeforeComma);
                    return;
                  }
                }

                // Handle comma-separated values on paste
                if (value.includes(",")) {
                  const commaSeparatedValues =
                    processCommaSeparatedValues(value);
                  if (commaSeparatedValues.length > 1) {
                    handleMultipleSelect(commaSeparatedValues);
                    return;
                  }
                }
              }}
              onPaste={(e) => {
                // Handle paste with comma-separated values
                const pastedText = e.clipboardData.getData("text");

                if (pastedText.includes(",")) {
                  e.preventDefault();
                  const commaSeparatedValues =
                    processCommaSeparatedValues(pastedText);
                  if (commaSeparatedValues.length > 0) {
                    handleMultipleSelect(commaSeparatedValues);
                  }
                }
              }}
              placeholder={displayPlaceholder}
              onFocus={handleFocus}
              onBlur={() => {
               
                  // Skip blur logic if clicking on dropdown
                  if (!isClickingDropdownRef.current) {
                    if (inputValue.trim()) {
                      handleSelect(inputValue.trim());
                    }
                    if (!showDropdown || !dropdownItems?.length) {
                      handleFieldActive(null);
                    }
                  }
                  isClickingDropdownRef.current = false;
              }}
              className="qs_search_input"
              id="qs_keywords_input"
              onKeyDown={(e) => {
                // Handle arrow key navigation when dropdown is visible
                if (showDropdown && dropdownItems.length > 0) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSelectedIndex((prev) => {
                      const nextIndex =
                        prev < dropdownItems.length - 1 ? prev + 1 : 0;
                      return nextIndex;
                    });
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSelectedIndex((prev) => {
                      // If at first element (0), do nothing
                      if (prev <= 0) {
                        return 0;
                      }
                      return prev - 1;
                    });
                    return;
                  }
                  if (
                    e.key === "Enter" &&
                    selectedIndex >= 0 &&
                    selectedIndex < dropdownItems.length
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelect(dropdownItems[selectedIndex]);
                    setSelectedIndex(-1);
                    return;
                  }
                  if (e.key === "Escape") {
                    setSelectedIndex(-1);
                    return;
                  }
                }

                if (
                  (e.key === "Enter" || e.key === "Tab") &&
                  inputValue.trim()
                ) {
                  e.preventDefault();

                  if(inputValue?.trim()){
                    e.stopPropagation();
                  }

                  // Check if input contains comma
                  if (inputValue.includes(",")) {
                    const commaSeparatedValues =
                      processCommaSeparatedValues(inputValue);
                    if (commaSeparatedValues.length > 0) {
                      handleMultipleSelect(commaSeparatedValues);
                    }
                  } else {
                    // Add single value on Enter/Tab
                    handleSelect(inputValue.trim());
                  }
                  return;
                }

                if (e.key === "Backspace" && !inputValue) {
                  e.preventDefault();
                  // Remove last chip on Backspace
                  setSelectedKeywords((prev) => prev.slice(0, -1));
                }
              }}
            />
          </div>
        </div>
        {showRightShadow && <div className="qs_overflow_shadow right" />}
      </div>

      <DropdownWrapper
        classNames={`qs_kd_wrapper ${
          dropdownType === "suggestion" ? "qs_kd_suggestions" : ""
        }`}
        isOpen={showDropdown && !!dropdownItems?.length}
        handleClose={handleCloseDropdown}
        handleFieldActive={handleFieldActive}
      >
        <KeywordsDropdown
          type={dropdownType}
          items={dropdownItems}
          onSelect={handleSelect}
          selectedIndex={selectedIndex}
          setSelectedIndex={setSelectedIndex}
        />
      </DropdownWrapper>

      {/* Error  */}
      {isError && (
        <div className="qs_search_keywords_error">
          250 characters limit has been exceeded!
        </div>
      )}

    </div>
  );
};

export default React.memo(Keywords);
